import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, ArrowUpRight, Star, BellRing } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { MaskedLines, Reveal } from "@/components/Motion";
import { EventCard } from "@/components/EventCard";
import EventDialog from "@/components/EventDialog";
import { CompanyAvatar } from "@/components/CompanyAvatar";
import { BriefCard } from "@/components/BriefCard";
import { SampleBadge } from "@/components/SampleBadge";
import { DeadlineChip } from "@/components/Tiers";
import { WinnerBadges } from "@/components/WinnerBadge";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const TYPE_LABELS = { Brand: "Brands", Sponsor: "Sponsors", Event: "Events", Creator: "Creators", Agency: "Agencies", Publisher: "Publishers", Venue: "Venues", "Sports Team": "Sports teams", "Media Company": "Media companies", Nonprofit: "Nonprofits", Investor: "Investors", "Hospitality Group": "Hospitality groups" };

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <label className="flex flex-col gap-1.5 min-w-0">
    <span className="text-[11px] uppercase tracking-[0.18em] text-slate-400">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} className="field !py-2.5" data-testid={testid}>
      <option value="">All</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

export const CompanyCard = ({ c, i = 0 }) => (
  <Reveal delay={(i % 3) * 0.06}>
    <Link to={`/companies/${c.id}`} className="group block h-full rounded-2xl border border-[#2A1240]/10 bg-[#FFFFFF] p-6 hover:border-[#E0218A]/40 transition-colors" data-testid={`company-card-${c.id}`}>
      <div className="flex items-start justify-between gap-4">
        <CompanyAvatar c={c} />
        <span className="flex items-center gap-2"><SampleBadge show={c.sample} testid={`company-card-sample-${c.id}`} /><span className="rounded-full border border-[#2A1240]/15 px-3 py-1 text-[11px] text-slate-200">{c.company_type}</span></span>
      </div>
      <h3 className="mt-5 font-display text-2xl text-[#2A1240] group-hover:text-[#8E2BC4] transition-colors">{c.name}</h3>
      <div className="mt-2"><WinnerBadges companyId={c.id} size="sm" testid={`company-card-badge-${c.id}`} /></div>
      {c.about && <p className="mt-2 text-sm text-slate-400 line-clamp-2">{c.about}</p>}
      <div className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-300">
        {c.industry && <span>{c.industry}</span>}
        {(c.locations?.length > 0 || c.region) && <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-[#E0218A]" />{c.locations?.[0] || c.region}</span>}
        {c.budget_range && <span className="font-mono text-[#8E2BC4]">{c.budget_range}</span>}
      </div>
    </Link>
  </Reveal>
);

export const OpportunityRow = ({ o, watching, onWatch }) => (
  <div className="group flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-6" data-testid={`opportunity-row-${o.id}`}>
    <Link to={`/companies/${o.company_id}`} className="min-w-0">
      <p className="text-xs text-slate-400 flex flex-wrap items-center gap-2">
        <SampleBadge show={o.sample} testid={`opportunity-sample-${o.id}`} />
        {o.featured && <span className="inline-flex items-center gap-1 rounded-full bg-[#E0218A] px-2 py-0.5 text-[10px] font-bold uppercase text-white"><Star className="h-3 w-3" />Featured</span>}
        {o.company_name} · {o.industry}
      </p>
      <p className="mt-1 font-display text-xl text-[#2A1240] group-hover:text-[#8E2BC4] transition-colors">{o.title}</p>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400">
        {o.tags?.length > 0 && <span>{o.tags.join(" · ")}</span>}
        {o.proposal_deadline && <span>Proposals {fmtDate(o.proposal_deadline)}</span>}
        {o.decision_date && <span>Decision {fmtDate(o.decision_date)}</span>}
        {o.campaign_date && <span>Campaign {fmtDate(o.campaign_date)}</span>}
      </div>
    </Link>
    <div className="flex flex-wrap items-center gap-3 text-sm shrink-0">
      <span className="font-mono text-[#8E2BC4]">{o.budget}</span>
      <DeadlineChip date={o.application_deadline || o.deadline} testid={`opportunity-deadline-${o.id}`} />
      {onWatch && <button onClick={() => onWatch(o)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors ${watching ? "bg-[#E0218A]/15 text-[#8E2BC4] border border-[#E0218A]/40" : "border border-[#2A1240]/15 text-slate-200 hover:border-[#E0218A]"}`} data-testid={`opportunity-watch-${o.id}`}><BellRing className="h-3.5 w-3.5" />{watching ? "Reminders on" : "Remind me"}</button>}
      <Link to={`/companies/${o.company_id}`} aria-label="Open"><ArrowUpRight className="h-5 w-5 text-slate-400 group-hover:text-[#E0218A] transition-colors" /></Link>
    </div>
  </div>
);

export default function Discover() {
  useSeo("Discover Brand Partners and Sponsors", "Search verified brands, sponsors, events, creators, agencies, venues and media companies by industry, location and budget.", "/discover");
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "companies";
  const [meta, setMeta] = useState({ company_types: [], industries: [], regions: [], budgets: [], countries: [] });
  const [country, setCountry] = useState(sp.get("country") || "");
  const [q, setQ] = useState(sp.get("q") || "");
  const [f, setF] = useState({ type: sp.get("type") || "", industry: "", region: "", budget: "" });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEv, setOpenEv] = useState(null);
  const { user } = useAuth();
  const [watching, setWatching] = useState([]);

  useEffect(() => {
    if (user) api.get("/opportunities/watching").then((r) => setWatching(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user]);

  const toggleWatch = async (o) => {
    if (!user) { window.location.href = "/login?next=/discover?tab=opportunities"; return; }
    const on = watching.includes(o.id);
    try {
      if (on) await api.delete(`/opportunities/${o.id}/watch`); else await api.post(`/opportunities/${o.id}/watch`);
      setWatching(on ? watching.filter((x) => x !== o.id) : [...watching, o.id]);
      toast.success(on ? "Reminders off" : "We'll remind you before each deadline");
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data || meta)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      const path = { opportunities: "/opportunities", events: "/events", briefs: "/briefs" }[tab] || "/companies";
      const params = tab === "companies" ? { q, country, ...f } : { q, country };
      api.get(path, { params })
        .then((r) => setData(Array.isArray(r.data) ? r.data : []))
        .catch(() => setData([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [tab, q, f, country]);

  const setTab = (t) => setSp(t === "companies" ? {} : { tab: t });

  return (
    <div className="min-h-screen bg-[#FFF7FB]" data-testid="discover-page">
      <Nav />
      <section className="pt-32 pb-10 mx-auto max-w-7xl px-5 sm:px-8">
        <p className="kicker">Discover</p>
        <MaskedLines as="h1" className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-[#2A1240] tracking-tight"
          lines={["The partnership", <em key="d" className="italic gold-text">directory.</em>]} />
        <Reveal className="mt-10">
          <div className="relative">
            <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#E0218A]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="What are you looking for?"
              className="w-full rounded-full bg-[#FFFFFF] border border-[#2A1240]/15 pl-14 pr-6 py-5 text-base sm:text-lg text-[#2A1240] placeholder:text-slate-400 outline-none focus:border-[#E0218A] transition-colors"
              data-testid="discover-search-input" />
          </div>
        </Reveal>
        <div className="mt-8 flex gap-2 overflow-x-auto no-scrollbar" data-testid="discover-tabs">
          {["companies", "briefs", "opportunities", "events"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`discover-tab-${t}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-[#2A1240] text-white" : "border border-[#2A1240]/15 text-slate-200 hover:border-[#E0218A]"}`}>{t}</button>
          ))}
        </div>
        <div className="mt-6 max-w-xs" data-testid="discover-country-wrap">
          <FilterSelect label="Country" value={country} onChange={setCountry} options={Array.isArray(meta.countries) ? meta.countries : []} testid="filter-country" />
        </div>
        {tab === "companies" && (
          <>
            <div className="mt-6 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="discover-type-chips">
              {meta.company_types.map((t) => (
                <button key={t} onClick={() => setF({ ...f, type: f.type === t ? "" : t })} data-testid={`type-chip-${t.toLowerCase().replace(/\s/g, "-")}`}
                  className={`shrink-0 rounded-full px-4 py-1.5 text-xs transition-colors ${f.type === t ? "bg-[#E0218A] text-white font-bold" : "bg-[#2A1240]/5 text-slate-200 hover:bg-[#2A1240]/10"}`}>
                  {TYPE_LABELS[t] || t}
                </button>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
              <FilterSelect label="Industry" value={f.industry} onChange={(v) => setF({ ...f, industry: v })} options={meta.industries} testid="filter-industry" />
              <FilterSelect label="Region" value={f.region} onChange={(v) => setF({ ...f, region: v })} options={meta.regions} testid="filter-location" />
              <FilterSelect label="Budget" value={f.budget} onChange={(v) => setF({ ...f, budget: v })} options={meta.budgets} testid="filter-budget" />
            </div>
          </>
        )}
      </section>
      <section className="pb-24 mx-auto max-w-7xl px-5 sm:px-8" data-testid="discover-results">
        <p className="text-sm text-slate-400 mb-6" data-testid="discover-results-count">{loading ? "Searching…" : `${data.length} result${data.length === 1 ? "" : "s"}`}</p>
        {!loading && !data.length && (
          <div className="rounded-2xl border border-dashed border-[#2A1240]/20 bg-white p-10 text-center" data-testid="discover-empty">
            <p className="text-[#2A1240] font-semibold">{tab === "companies" ? "No companies here yet." : `No ${tab} here yet.`} Be the first.</p>
            <p className="mt-2 text-sm text-slate-300">Try a broader search, or put your own {tab === "events" ? "event" : tab === "briefs" ? "partnership brief" : tab === "opportunities" ? "opportunity" : "company"} in front of partners in every country.</p>
            <Link to={tab === "companies" ? "/register" : tab === "events" ? "/app/events" : tab === "briefs" ? "/app/briefs" : "/app/network"} className="btn-gold mt-5" data-testid="discover-empty-cta">{tab === "companies" ? "List your company free" : tab === "events" ? "Post an event" : tab === "briefs" ? "Publish a brief" : "Post an opportunity"}</Link>
          </div>
        )}
        {tab === "companies" && <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{data.map((c, i) => <CompanyCard key={c.id} c={c} i={i} />)}</div>}
        {tab === "opportunities" && <div className="divide-y divide-[#2A1240]/10 border-y border-[#2A1240]/10">{data.map((o) => <OpportunityRow key={o.id} o={o} watching={watching.includes(o.id)} onWatch={toggleWatch} />)}</div>}
        {tab === "briefs" && <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{data.map((b, i) => <BriefCard key={b.id} b={b} i={i} />)}</div>}
        {tab === "events" && <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{data.map((e, i) => <EventCard key={e.id} e={e} i={i} onOpen={setOpenEv} />)}</div>}
      </section>
      <EventDialog event={openEv} onClose={() => setOpenEv(null)} />
      <Footer />
    </div>
  );
}
